/**
 * The complete list of languages supported by the translation system.
 * Single source of truth — lives in @paintscout/util so both API (server) and
 * UI (client) can import it without pulling in browser-only dependencies.
 */
export const SUPPORTED_LANGS = ['en', 'es', 'fr'] as const
export type SupportedLang = (typeof SUPPORTED_LANGS)[number]

const NON_ENGLISH_SUPPORTED_LANGS = SUPPORTED_LANGS.filter((lang) => lang !== 'en')

export function getEnabledTranslationLangs(flagValue: unknown): string[] {
  if (!Array.isArray(flagValue)) return []

  const enabledLangs = new Set(
    flagValue.filter(
      (lang): lang is (typeof NON_ENGLISH_SUPPORTED_LANGS)[number] =>
        typeof lang === 'string' &&
        NON_ENGLISH_SUPPORTED_LANGS.includes(lang as (typeof NON_ENGLISH_SUPPORTED_LANGS)[number])
    )
  )

  return NON_ENGLISH_SUPPORTED_LANGS.filter((lang) => enabledLangs.has(lang))
}

/**
 * Resolves the enabled work-order translation languages for a company from the two-layer model:
 *
 * - The LaunchDarkly `translations` flag is the **beta gate**. If it grants no languages
 *   (null/empty), the company is not in the beta and translations are off — full stop.
 * - Within the beta, the per-company DB setting (`options.options.translations`) is the
 *   authoritative language list. When it is unset/empty it falls back to the flag's languages,
 *   so existing flag-only companies keep behaving exactly as before (no migration needed).
 *
 * Both inputs are validated against SUPPORTED_LANGS, so unknown/unsupported codes are ignored
 * and the DB setting can never enable an unsupported language.
 *
 * @param ldFlagValue   raw LaunchDarkly `translations` flag value (e.g. ['es','fr'] | null)
 * @param companyTranslations  raw per-company DB setting (options.options.translations)
 */
export function resolveAvailableTranslationLangs(ldFlagValue: unknown, companyTranslations?: unknown): string[] {
  const ldLangs = getEnabledTranslationLangs(ldFlagValue)
  // LD gates the beta: no flag languages => translations off regardless of the DB setting.
  if (ldLangs.length === 0) return []

  const dbLangs = getEnabledTranslationLangs(companyTranslations)
  return dbLangs.length ? dbLangs : ldLangs
}

/**
 * Strips a pasted browser-URL suffix (e.g. "?view=work-order" or a "#" fragment) off a quote id,
 * so support staff pasting the id straight out of the address bar doesn't 404 the lookup.
 * Quote ids are UUIDs and never contain "/", so — unlike an earlier version of this — this does
 * NOT split on "/": doing so would silently truncate any id that happened to contain one instead
 * of just rejecting it, which is the wrong failure mode for a support tool. Shared between the
 * admin UI and the API resolver so the two can't drift.
 */
export function sanitizeQuoteId(rawQuoteId: string): string {
  return rawQuoteId.trim().split(/[?#]/)[0]
}
